import { useCallback, useEffect, useMemo, useState } from "react";
import { Radio, Plus, Pencil, Trash2, MapPin, Search, Download, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import WifiForm from "@/components/wifi/WifiForm";
import PlanningPanel from "@/components/wifi/PlanningPanel";
import MonitoringPanel from "@/components/wifi/MonitoringPanel";
import { COLUMNS, RADIO_CATS, getPath } from "@/components/wifi/wifiFields";
import { WIFI_TABS } from "@/lib/wifiTabs";

const TABS = WIFI_TABS;
const KIND = { sites: "sites", sectors: "devices", links: "links", devices: "devices", models: "models", subscribers: "installations", ip: "subnets" };

function toCsv(rows, cols) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return "\ufeff" + [cols.map((c) => esc(c[1])).join(";"), ...rows.map((r) => cols.map((c) => esc(getPath(r, c[0]))).join(";"))].join("\n");
}
function saveBlob(text, name, type) {
  const url = URL.createObjectURL(new Blob([text], { type })); const a = document.createElement("a");
  a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
}
function geojson(kind, rows) {
  const feats = rows.flatMap((r) => {
    if (kind === "links" && r.calc?.distance_m != null) return [{ type: "Feature", properties: { name: r.name, status: r.status, distance_m: r.calc.distance_m }, geometry: { type: "LineString", coordinates: [[r.calc.a.lng, r.calc.a.lat], [r.calc.b.lng, r.calc.b.lat]] } }];
    if (r.lat != null) return [{ type: "Feature", properties: { name: r.name, status: r.status, category: r.category, azimuth: r.azimuth }, geometry: { type: "Point", coordinates: [r.lng, r.lat] } }];
    return [];
  });
  return JSON.stringify({ type: "FeatureCollection", features: feats }, null, 1);
}

function Dashboard({ d }) {
  if (!d) return null;
  const tiles = [["Bazy", d.sites], ["Aktywne nadajniki", d.transmitters_active], ["Sektory", d.sectors], ["Mosty PTP", d.ptp_links],
    ["CPE / abonenci radiowi", d.cpe], ["Urządzenia", d.devices], ["Awaria / serwis", d.devices_failure], ["Planowane", d.planned]];
  const max = Math.max(1, ...d.by_site.map((s) => s.devices));
  return (
    <div className="p-4 space-y-4" data-testid="wifi-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {tiles.map(([l, v]) => <div key={l} className="bg-slate-800/70 rounded p-2.5"><div className="text-[10px] text-slate-400 uppercase">{l}</div><div className="text-xl font-bold font-mono">{v}</div></div>)}
      </div>
      <div><div className="text-xs font-semibold text-slate-300 mb-1">Urządzenia i CPE wg baz</div>
        {d.by_site.length === 0 && <div className="text-xs text-slate-500">Brak baz</div>}
        {d.by_site.map((s) => (
          <div key={s.name} className="flex items-center gap-2 text-[11px] py-0.5"><span className="w-32 truncate">{s.name}</span>
            <div className="flex-1 bg-slate-800 rounded h-2"><div className="bg-purple-500 h-2 rounded" style={{ width: `${(s.devices / max) * 100}%` }} /></div>
            <span className="font-mono w-16 text-right">{s.devices} / {s.cpe} CPE</span></div>
        ))}</div>
      <div><div className="text-xs font-semibold text-slate-300 mb-1">Rozkład częstotliwości</div>
        <div className="flex flex-wrap gap-2">{d.by_band.map((b) => <span key={b.band} className="text-[11px] bg-slate-800 rounded px-2 py-1">{b.band} GHz: <b>{b.count}</b></span>)}</div></div>
      <div className="text-[11px] text-amber-300 bg-amber-950/30 border border-amber-800/50 rounded p-2">{d.monitoring}</div>
    </div>
  );
}

function Audit() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/wifi/audit").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="p-3 space-y-1.5" data-testid="wifi-audit">
      {rows.length === 0 && <div className="text-xs text-slate-500">Brak zmian</div>}
      {rows.map((r) => (
        <div key={r.id} className="bg-slate-800/60 rounded p-2 text-[11px]">
          <div className="flex justify-between"><span><b>{r.action}</b> · {r.kind}{r.name ? ` · ${r.name}` : ""}</span><span className="text-slate-500">{r.at.slice(0, 16).replace("T", " ")} · {r.user}</span></div>
          {r.changes.filter((c) => !c.field.endsWith("_id")).slice(0, 12).map((c) => <div key={c.field} className="text-slate-400 font-mono">{c.field}: {String(c.before ?? "—")} → {String(c.after ?? "—")}</div>)}
        </div>
      ))}
    </div>
  );
}

function IpSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const run = () => q.trim() && api.get(`/wifi/ip/search?q=${encodeURIComponent(q.trim())}`).then((r) => setRes(r.data)).catch(() => {});
  return (
    <div className="m-3 p-3 rounded border border-slate-700 bg-slate-800/40 text-[11px]" data-testid="wifi-ip-search">
      <div className="font-semibold mb-1.5">Szukaj adresu IP / MAC</div>
      <div className="flex gap-2"><input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="10.10.0.15 lub AA:BB:..." data-testid="wifi-ip-search-input" className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 outline-none" />
        <button onClick={run} data-testid="wifi-ip-search-btn" className="bg-cyan-600 hover:bg-cyan-500 rounded px-2">Szukaj</button></div>
      {res && (<div className="mt-2 space-y-1">
        {res.devices.map((d) => <div key={d.id} className="font-mono">{d.ip || "—"} · {d.mac || "—"} · {d.name} ({d.category})</div>)}
        {res.subnets.map((n) => <div key={n.id} className="text-cyan-300">Podsieć: {n.cidr} {n.name || ""} · VLAN {n.vlan ?? "—"}</div>)}
        {!res.devices.length && !res.subnets.length && <div className="text-slate-500">Brak wyników — adres wolny</div>}
        {res.duplicates.map((d) => <div key={d.ip} className="text-red-400">Duplikat IP {d.ip}: {d.names.join(", ")}</div>)}
      </div>)}
    </div>
  );
}

export default function WifiPanel({ role, onClose, onChanged, onLocate, mapCenter, nav }) {
  const [tab, setTab] = useState(nav?.tab || "dashboard");
  useEffect(() => {
    if (nav?.tab) {
      setTab(nav.tab); setQ("");
      setTimeout(() => { const el = document.querySelector(`[data-testid="wifi-tab-${nav.tab}"]`); if (el) el.scrollIntoView({ inline: "nearest", block: "nearest" }); }, 50);
    }
  }, [nav]);
  const [data, setData] = useState({ sites: [], devices: [], links: [], models: [], installations: [], subnets: [], clients: [] });
  const [dash, setDash] = useState(null);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const canEdit = role !== "odczyt";
  const canDelete = role === "admin" || role === "projektant";

  const load = useCallback(async () => {
    const keys = ["sites", "devices", "links", "models", "installations", "subnets", "dashboard"];
    const res = await Promise.all(keys.map((k) => api.get(`/wifi/${k}`).then((r) => r.data).catch(() => (k === "dashboard" ? null : []))));
    const clients = await api.get("/clients").then((r) => r.data).catch(() => []);
    const o = Object.fromEntries(keys.map((k, i) => [k, res[i]]));
    setData({ ...o, clients }); setDash(o.dashboard);
  }, []);
  useEffect(() => { load(); }, [load]);

  const kind = KIND[tab];
  const rows = useMemo(() => {
    let r = kind ? data[kind] : [];
    if (tab === "sectors") r = r.filter((x) => RADIO_CATS.includes(x.category));
    if (q) { const s = q.toLowerCase(); r = r.filter((x) => JSON.stringify(x).toLowerCase().includes(s)); }
    return r;
  }, [data, kind, tab, q]);

  const del = async (it) => {
    if (!window.confirm(`Usunąć „${it.name || it.model}”?`)) return;
    try { await api.delete(`/wifi/${kind}/${it.id}`); toast.success("Usunięto"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pending = TABS.find((t) => t[0] === tab)?.[2];
  const mkTask = async (r) => {
    try { const { data: d } = await api.post(`/wifi/installations/${r.id}/task`, {}); toast.success(`Utworzono zlecenie ${d.task_no || ""} — przypisz montera w Terminarzu`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cols = kind ? COLUMNS[kind] : [];

  return (
    <div className="h-full flex flex-col bg-slate-900" data-testid="wifi-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
        <div className="flex items-center gap-2"><Radio size={18} className="text-purple-400" /><h2 className="font-bold text-base">Sieć WiFi</h2></div>
      </div>
      <div className="flex gap-1 px-3 py-2 border-b border-slate-800 overflow-x-auto">
        {TABS.map(([k, l, st]) => (
          <button key={k} onClick={() => { setTab(k); setQ(""); }} data-testid={`wifi-tab-${k}`}
            className={`shrink-0 text-[11px] rounded px-2.5 py-1 border ${tab === k ? "border-purple-500 bg-purple-500/15 text-purple-200" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
            {l}{st ? <span className="ml-1 text-[9px] text-amber-400">etap {st}</span> : null}
          </button>
        ))}
      </div>
      {tab === "dashboard" && <div className="flex-1 overflow-y-auto"><Dashboard d={dash} /></div>}
      {tab === "audit" && <div className="flex-1 overflow-y-auto"><Audit /></div>}
      {tab === "monitoring" && <MonitoringPanel devices={data.devices} canManage={canDelete} />}
      {tab === "planning" && <PlanningPanel data={data} canEdit={canEdit} onChanged={() => { load(); onChanged && onChanged(); }} />}
      {pending && (
        <div className="p-6 text-sm text-slate-400" data-testid="wifi-pending-stage">
          <Clock className="text-amber-400 mb-2" size={22} />
          Ta część modułu powstaje w <b>etapie {pending}</b> i nie jest jeszcze dostępna — nic tu nie jest symulowane.
        </div>
      )}
      {kind && (
        <>
          <div className="px-3 py-2 border-b border-slate-800 flex gap-2">
            <div className="relative flex-1"><Search size={13} className="absolute left-2 top-2 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj (nazwa, IP, MAC…)" data-testid="wifi-search"
                className="w-full bg-slate-800 border border-slate-700 rounded pl-7 pr-2 py-1 text-xs outline-none focus:ring-1 focus:ring-cyan-500" /></div>
            <button onClick={() => saveBlob(toCsv(rows, cols), `wifi-${tab}.csv`, "text/csv")} data-testid="wifi-export-csv" title="CSV" className="px-2 rounded hover:bg-slate-800 text-slate-300 text-[11px] flex items-center gap-1"><Download size={12} />CSV</button>
            {kind !== "models" && <button onClick={() => saveBlob(geojson(kind, rows), `wifi-${tab}.geojson`, "application/geo+json")} data-testid="wifi-export-geojson" className="px-2 rounded hover:bg-slate-800 text-slate-300 text-[11px] flex items-center gap-1"><Download size={12} />GeoJSON</button>}
            {canEdit && <button onClick={() => setForm({ initial: tab === "sectors" ? { category: "Sector AP", beamwidth_h: 90, range_m: 3000 } : null })} data-testid="wifi-add-btn"
              className="bg-purple-600 hover:bg-purple-500 rounded px-2.5 flex items-center gap-1 text-xs"><Plus size={13} />Dodaj</button>}
          </div>
          <div className="flex-1 overflow-auto">
            <table className="w-full text-[11px]" data-testid={`wifi-table-${tab}`}>
              <thead className="sticky top-0 bg-slate-900"><tr>{cols.map((c) => <th key={c[0]} className="text-left px-2 py-1.5 text-slate-400 font-medium whitespace-nowrap">{c[1]}</th>)}<th /></tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={cols.length + 1} className="text-center text-slate-500 py-8">Brak rekordów</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-800 hover:bg-slate-800/50 align-top" data-testid={`wifi-row-${r.id}`} title={kind === "installations" ? (r.calc?.path || []).join(" → ") : undefined}>
                    {cols.map((c) => <td key={c[0]} className="px-2 py-1.5 whitespace-nowrap">{String(getPath(r, c[0]) ?? "—")}</td>)}
                    <td className="px-1 whitespace-nowrap text-right">
                      {kind === "installations" && canDelete && !r.task_id && <button onClick={() => mkTask(r)} data-testid={`wifi-task-${r.id}`} title="Utwórz zlecenie instalacji" className="p-1 rounded hover:bg-slate-700 text-amber-300 text-[10px]">+ zlecenie</button>}
                      {kind === "installations" && r.task_id && <span className="text-[10px] text-emerald-400 mr-1">zlecenie ✓</span>}
                      {onLocate && (r.lat != null || r.calc?.a?.lat != null) && <button onClick={() => onLocate(r.lat != null ? [r.lat, r.lng] : [r.calc.a.lat, r.calc.a.lng])} data-testid={`wifi-locate-${r.id}`} className="p-1 rounded hover:bg-slate-700"><MapPin size={12} /></button>}
                      {canEdit && <button onClick={() => setForm({ initial: r })} data-testid={`wifi-edit-${r.id}`} className="p-1 rounded hover:bg-slate-700"><Pencil size={12} /></button>}
                      {canDelete && <button onClick={() => del(r)} data-testid={`wifi-delete-${r.id}`} className="p-1 rounded hover:bg-red-600/30 text-red-400"><Trash2 size={12} /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {kind === "links" && <div className="text-[10px] text-slate-500 px-3 py-2">* RSSI szacunkowy (budżet łącza bez profilu terenu). Kolumna LOS — wynik ostatniej analizy terenu z zakładki Planowanie radiowe.</div>}
            {kind === "installations" && <div className="text-[10px] text-slate-500 px-3 py-2">Droga radiowa (CPE → sektor → baza) w dymku wiersza. Azymut liczony z pozycji w systemie; monter dostaje dokładny kierunek z GPS i kompasu telefonu w zleceniu.</div>}
            {kind === "subnets" && <IpSearch />}
          </div>
        </>
      )}
      {form && (
        <WifiForm kind={kind} initial={form.initial} refs={{ sites: data.sites, devices: data.devices, models: data.models, links: data.links, clients: data.clients }} mapCenter={mapCenter}
          onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); onChanged && onChanged(); }} />
      )}
    </div>
  );
}
